import { useTranslation } from 'react-i18next';
import { faCar } from '@fortawesome/pro-regular-svg-icons/faCar';
import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons/faSpinnerThird';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Spin, theme } from 'antd';
import { useJobStatusTextColor } from 'pos-client/components/scheduleBoard/hooks/useJobStatusTextColor.ts';
import { useOrderVehicleDetails } from 'pos-client/components/scheduleBoard/hooks/useOrderVehicleDetails.ts';
import { VehicleDetails } from 'pos-client/components/scheduleBoard/types/boardTypes.ts';
import type { ScheduleBoardJobModel } from 'pos-shared/models/schedulingSchema.ts';

import { JobSlotDetail } from './JobSlotDetail.tsx';

interface OrderVehicleDetailsListProps {
  appointmentData: ScheduleBoardJobModel;
}

export const OrderVehicleDetailsList = ({
  appointmentData,
}: OrderVehicleDetailsListProps) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const textColor = useJobStatusTextColor(appointmentData.state);

  const properties = [
    VehicleDetails.PLATE,
    VehicleDetails.MAKE,
    VehicleDetails.MODEL,
    VehicleDetails.COLOR,
  ].map((property) => property.toLowerCase());

  const { orderVehicleDetails, orderVehicleDetailsLoading } =
    useOrderVehicleDetails((appointmentData as ScheduleBoardJobModel).orderId);

  const details = orderVehicleDetails.filter(
    (vehicleData, index, self) =>
      // filter out data that has the same key, e.g. if two items have a key of 'Model', display the first only
      self.findIndex(
        (vehicle) =>
          vehicle.key?.toLowerCase() === vehicleData.key?.toLowerCase()
      ) === index &&
      properties.includes(vehicleData.key?.toLowerCase() as string)
  );

  // order in which the values should appear
  const order = ['Plate', 'Make', 'Model', 'Color'];

  if (
    details.some((detail) => detail.key !== 'Plate') &&
    appointmentData?.fulfilmentTo?.reference
  ) {
    details.unshift({
      key: 'Plate',
      value: appointmentData.fulfilmentTo.reference,
    });
  }

  const sortedDetails = details.sort((a, b) => {
    const indexA = order.indexOf(a.key || '');
    const indexB = order.indexOf(b.key || '');
    return indexA - indexB;
  });

  const detailsList = sortedDetails.map((item) => item.value || '');

  if (orderVehicleDetailsLoading && !orderVehicleDetails.length) {
    return (
      <Spin
        size="small"
        style={{ marginBottom: token.marginMD }}
        indicator={
          <FontAwesomeIcon
            icon={faSpinnerThird}
            style={{ color: textColor }}
            spin
          />
        }
      />
    );
  }

  if (!orderVehicleDetailsLoading && !orderVehicleDetails.length) {
    return (
      <JobSlotDetail
        detailsList={[t('common.vehicle.vehicleNotFound')]}
        icon={faCar}
      />
    );
  }

  return <JobSlotDetail detailsList={detailsList} icon={faCar} />;
};
